// Printed insurer documents (docs/plans/phase2-private-insurance.md §6 items 5 and 6): the
// claim form for one claim, and the invoice listing an insurer's claims. A4, black on
// white, identifiers in the mono face. The claim form carries diagnoses, so printing it
// is audited (CLAUDE.md: prints of clinical documents).
//
// TODO(business): each insurer has its own claim form; this is Wodi's generic one until
// facilities share the forms their insurers require.

import { useMutation } from '@tanstack/react-query';
import { formatMoney, moneyFromCents } from '@wodi/domain';
import type { AmountView, InsurerClaimDetail, InsurerInvoiceDetail } from '@wodi/shared';
import {
  Button,
  PrintFooter,
  PrintHeader,
  PrintIdentifier,
  PrintLines,
  PrintSheet,
  PrintTable,
  Section,
  printSheet,
} from '@wodi/ui';
import { useRef } from 'react';

import { markPrinted } from '../api/insurance.ts';
import { formatDateOnly, formatIsoDate } from '../patients/format.ts';
import styles from './insurance.module.css';

const kes = (a: AmountView) => formatMoney(moneyFromCents(BigInt(a.cents)));

export function ClaimFormSheet({ claim }: { claim: InsurerClaimDetail }) {
  const sheet = useRef<HTMLElement>(null);
  const printed = useMutation({ mutationFn: () => markPrinted(claim.id) });
  const cover = claim.cover;
  const total = claim.lines.reduce((s, l) => s + BigInt(l.payer.cents), 0n);
  return (
    <Section
      title="Claim form"
      actions={
        <Button
          onPress={() => {
            if (sheet.current === null) return;
            printSheet(sheet.current);
            printed.mutate();
          }}
        >
          Print claim form
        </Button>
      }
    >
      <PrintSheet paper="a4" label="Claim form" ref={sheet}>
        <PrintHeader
          facility={claim.facility.name}
          facilityLines={
            claim.facility.mflCode === null ? [] : [`MFL code ${claim.facility.mflCode}`]
          }
          title={`Claim to ${claim.payer.name}`}
        />
        <PrintLines
          items={[
            { label: 'Claim number', value: claim.claimNumber, identifier: true },
            ...(claim.invoiceNumber === null
              ? []
              : [{ label: 'Invoice number', value: claim.invoiceNumber, identifier: true }]),
            { label: 'Scheme', value: claim.schemeName },
            { label: 'Member number', value: cover.memberNumber ?? '', identifier: true },
            ...(cover.relationship === 'dependant'
              ? [
                  { label: 'Principal member', value: cover.principalName ?? 'Not recorded' },
                  {
                    label: 'Principal member number',
                    value: cover.principalMemberNumber ?? '',
                    identifier: true,
                  },
                ]
              : [{ label: 'Relationship', value: 'Principal member' }]),
            { label: 'Patient', value: claim.patient.displayName },
            { label: 'Patient number', value: claim.patient.patientNumber, identifier: true },
            { label: 'Visit', value: claim.visit.visitNumber, identifier: true },
            { label: 'Visit date', value: formatDateOnly(claim.visit.startedAt) },
          ]}
        />
        <PrintTable>
          <thead>
            <tr>
              <th>Diagnosis</th>
              <th>Code</th>
            </tr>
          </thead>
          <tbody>
            {claim.diagnoses.length === 0 ? (
              <tr>
                <td colSpan={2}>No diagnosis recorded</td>
              </tr>
            ) : (
              claim.diagnoses.map((d) => (
                <tr key={`${d.code ?? ''}-${d.display}`}>
                  <td>{d.display}</td>
                  <td>{d.code === null ? '' : <PrintIdentifier>{d.code}</PrintIdentifier>}</td>
                </tr>
              ))
            )}
          </tbody>
        </PrintTable>
        <PrintTable>
          <thead>
            <tr>
              <th>Date</th>
              <th>Service</th>
              <th>Qty</th>
              <th>Unit price</th>
              <th>Patient pays</th>
              <th>Claimed</th>
              <th>Approval</th>
            </tr>
          </thead>
          <tbody>
            {claim.lines.map((l) => (
              <tr key={l.chargeId}>
                <td>{formatIsoDate(l.serviceDate)}</td>
                <td>{`${l.itemName} (${l.itemCode})`}</td>
                <td>{l.quantity}</td>
                <td>{kes(l.unitPrice)}</td>
                <td>{kes(l.patient)}</td>
                <td>{kes(l.payer)}</td>
                <td>
                  {l.preauthReference === null ? (
                    ''
                  ) : (
                    <PrintIdentifier>{l.preauthReference}</PrintIdentifier>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </PrintTable>
        <PrintLines
          items={[{ label: 'Total claimed', value: formatMoney(moneyFromCents(total)) }]}
        />
        <div className={styles.signature}>
          <span>Member or patient signature</span>
          <span>For the facility: name, signature and stamp</span>
        </div>
        <PrintFooter>Printed from Wodi. Amounts in Kenya shillings.</PrintFooter>
      </PrintSheet>
    </Section>
  );
}

export function InvoiceSheet({ invoice }: { invoice: InsurerInvoiceDetail }) {
  const sheet = useRef<HTMLElement>(null);
  return (
    <Section
      title="Invoice to the insurer"
      actions={
        <Button
          onPress={() => {
            if (sheet.current !== null) printSheet(sheet.current);
          }}
        >
          Print invoice
        </Button>
      }
    >
      <PrintSheet paper="a4" label="Invoice to the insurer" ref={sheet}>
        <PrintHeader
          facility={invoice.facility.name}
          facilityLines={
            invoice.facility.mflCode === null ? [] : [`MFL code ${invoice.facility.mflCode}`]
          }
          title={`Invoice to ${invoice.payer.name}`}
        />
        <PrintLines
          items={[
            { label: 'Invoice number', value: invoice.invoiceNumber, identifier: true },
            { label: 'Date', value: formatDateOnly(invoice.issuedAt) },
            { label: 'Claims', value: String(invoice.claimCount) },
          ]}
        />
        <PrintTable>
          <thead>
            <tr>
              <th>Claim</th>
              <th>Member</th>
              <th>Patient</th>
              <th>Visit date</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.claims.map((c) => (
              <tr key={c.id}>
                <td>
                  <PrintIdentifier>{c.claimNumber}</PrintIdentifier>
                </td>
                <td>
                  {c.memberNumber === null ? (
                    ''
                  ) : (
                    <PrintIdentifier>{c.memberNumber}</PrintIdentifier>
                  )}
                </td>
                <td>{c.patient?.displayName ?? 'Restricted record'}</td>
                <td>{formatDateOnly(c.visit.startedAt)}</td>
                <td>{kes(c.claimed)}</td>
              </tr>
            ))}
          </tbody>
        </PrintTable>
        <PrintLines items={[{ label: 'Total', value: kes(invoice.total) }]} />
        <PrintFooter>
          Printed from Wodi. Amounts in Kenya shillings. Not an eTIMS tax invoice.
        </PrintFooter>
      </PrintSheet>
    </Section>
  );
}
